import { IDataEntry, IDataSet } from 'components/Graphs/types/types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import 'dayjs/locale/pt-br'

export const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
// dayjs.locale('pt-br')
dayjs.extend(utc)
dayjs.extend(timezone)
const now = dayjs().tz(timeZone)

/**
 * Process time intervals for the given data.
 * @param data - The data entries to process.
 * @param intervalCount - The number of intervals to create.
 * @param intervalSizeMs - The size of each interval in milliseconds.
 * @param formatter - A function to format the interval labels.
 * @param nowDate - The current date to use for calculations.
 * @returns An array of processed data sets.
 */
const processTimeIntervals = (
  data: IDataEntry[],
  intervalCount: number,
  intervalSizeMs: number,
  formatter: (index: number) => string,
  nowDate: dayjs.Dayjs,
  floatingAvg: boolean = false
): IDataSet[] => {
  const grouped: number[][] = Array.from({ length: intervalCount }, () => [])

  data.forEach(entry => {
    const createdAt = dayjs(entry.created_at).tz(timeZone)
    const nowLocal = nowDate.clone().tz(timeZone) // Mantém consistência no timezone
    const elapsed = nowLocal.diff(createdAt) // Diferenca no mesmo fuso horário
    // const elapsed = nowDate.diff(createdAt)
    if (elapsed >= 0 && elapsed < intervalCount * intervalSizeMs) {
      const index = Math.floor(elapsed / intervalSizeMs)
      grouped[index].push(entry.value)
    }
  })

  const formatAvgValue = (values: number[]) => {
    const avg = Number(
      values.reduce((a, b) => a + Number(b), 0) / values.length
    )
    if (floatingAvg) {
      return avg
    }

    return Math.floor(avg)
  }

  return grouped
    .map((values, index) => {
      return {
        label: formatter(index),
        limits:
          values.length > 0
            ? [
                Math.max(...values.filter(v => !Number.isNaN(v))),
                Math.min(...values.filter(v => !Number.isNaN(v)))
              ]
            : null,
        avg: values.length > 0 ? formatAvgValue(values) : null
      }
    })
    .reverse()
}

/**
 * Group data by last 24 hours (split hour by hour).
 * @param data - The data entries to process.
 * @param refDate - The reference date to use for grouping.
 * @returns An array of processed data sets.
 */
export const groupLast24Hours = (
  data: IDataEntry[],
  refDate: dayjs.Dayjs = now,
  floatingAvg: boolean = false
) =>
  processTimeIntervals(
    data,
    24,
    60 * 60 * 1000, // 1 hora em ms
    index => refDate.subtract(0 + index, 'hour').format('HH[h]'),
    refDate,
    floatingAvg
  )

/**
 * Group data by last 7 days (split day by day).
 * @param data - The data entries to process.
 * @param refDate - The reference date to use for grouping.
 * @returns An array of processed data sets.
 */
export const groupLast7Days = (
  data: IDataEntry[],
  refDate: dayjs.Dayjs = now,
  floatingAvg: boolean = false
) => {
  const endOfDay = refDate.set('hour', 23).set('minute', 59).set('second', 59)

  return processTimeIntervals(
    data,
    7,
    24 * 60 * 60 * 1000, // 1 dia em ms
    index =>
      dayjs()
        .subtract(0 + index, 'day')
        .format('DD/MM (ddd)'), // Subtrai dias com Day.js
    endOfDay,
    floatingAvg
  )
}

/**
 * Group data by last 30 days (split day by day).
 * @param data - The data entries to process.
 * @param refDate - The reference date to use for grouping.
 * @returns An array of processed data sets.
 */
export const groupLast30Days = (
  data: IDataEntry[],
  refDate: dayjs.Dayjs = now,
  floatingAvg: boolean = false
) =>
  processTimeIntervals(
    data,
    30,
    24 * 60 * 60 * 1000, // 1 dia em ms
    index =>
      dayjs()
        .subtract(0 + index, 'day')
        .format('DD/MM'), // Subtrai dias com Day.js
    refDate,
    floatingAvg
  )

/**
 * Group data by last 12 months (split month by month).
 * @param data - The data entries to process.
 * @param refDate - The reference date to use for grouping.
 * @returns An array of processed data sets.
 */
export const groupLast12Months = (
  data: IDataEntry[],
  refDate: dayjs.Dayjs = now
) => {
  const grouped: number[][] = Array.from({ length: 12 }, () => [])

  data.forEach(entry => {
    const createdAt = new Date(entry.created_at)
    const diffMonths = dayjs(refDate).diff(dayjs(createdAt), 'month')

    if (diffMonths >= 0 && diffMonths < 12) {
      grouped[11 - diffMonths].push(entry.value)
    }
  })

  return grouped.map((values, index) => {
    const sortedList = values.sort((a, b) => a - b)

    const sum = values.reduce((a, b) => Number(a) + Number(b), 0)
    const length = values.length
    const avg = (length > 0 ? sum / length : 0).toFixed(2)

    const date = dayjs(refDate).subtract(11 - index, 'month')
    return {
      label: date.format('MMM'),
      limits:
        values.length > 0
          ? [sortedList[sortedList.length - 1], sortedList[0]]
          : null,
      avg: values.length > 0 ? Number(avg) : null
    }
  })
}
